import React, { useEffect, useState } from 'react';
import { Checkbox, DialogContent, DialogContentText, FormControlLabel, FormGroup } from '@mui/material';

import { OrganizationMaterialSet } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUserOrganization } from '@paragon/login-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { runWithSpinner } from '@paragon/task-spinner';

import { downloadPlatePriceComparisonCsv } from '../../../store/actions/downloadActions';
import { getCommittedComponentDesignsByGuid } from '../../../store/selectors/getComponentDesignsSelectors';
import { getProjectLayoutModel } from '../../../store/selectors/getProjectLayoutModel';
import { useAppDispatch, useAppSelector } from '../../hooks';

export function DownloadPlateListComparisonDialog() {
  const dispatch = useAppDispatch();

  const layoutModel = useAppSelector(getProjectLayoutModel);
  const componentDesignGuids = Object.keys(useAppSelector(getCommittedComponentDesignsByGuid));
  const organization = useAppSelector(getLoggedInUserOrganization);

  const [selectedPlateListGuids, setSelectedPlateListGuids] = useState<string[]>([]);
  const [organizationMaterialSet, setOrganizationMaterialSet] = useState<OrganizationMaterialSet | null>(null);

  // Sometimes multiple organizations' material sets are loaded; we only want the current user's organization's material set.
  useEffect(() => {
    dispatch(
      runWithSpinner('Loading plate lists...', async () => {
        setOrganizationMaterialSet(await designService.materialLists.getOrganizationMaterialSet(organization!));
      }),
    );
  }, [dispatch, organization]);

  if (organizationMaterialSet == null) {
    return null;
  }

  // Exclude Global Plate List since it will have more than one price
  // This GUID is also hardcoded in \servers\libraries\AggregationModels\AggregationModels.Serialization\MigrationData\InventorySets\Data\GlobalMaterials.cs
  const plateLists = Object.values(organizationMaterialSet.plateLists).filter(
    (pl) => pl.guid !== '236574a1-642e-4a79-a94f-ad8000b3f3ef',
  );

  return (
    <Dialog
      title="Select Plate Lists"
      open
      submitDisabled={selectedPlateListGuids.length === 0}
      onSubmit={() => {
        dispatch(
          downloadPlatePriceComparisonCsv({
            projectName: layoutModel!.name,
            componentDesignGuids,
            plateListGuids: selectedPlateListGuids,
          }),
        );
        dispatch(closeCurrentPopup());
      }}
      onCancel={() => void dispatch(closeCurrentPopup())}
      variant="design"
      submitText="Download"
    >
      <DialogContent>
        <DialogContentText>Plate lists against which to compare:</DialogContentText>
        <FormGroup>
          {plateLists.map((plateList) => (
            <FormControlLabel
              key={plateList.guid}
              control={
                <Checkbox
                  checked={selectedPlateListGuids.includes(plateList.guid)}
                  onChange={(e) =>
                    setSelectedPlateListGuids((prev) =>
                      e.target.checked ? [...prev, plateList.guid] : prev.filter((g) => g !== plateList.guid),
                    )
                  }
                  name={plateList.name}
                />
              }
              label={plateList.name}
            />
          ))}
        </FormGroup>
      </DialogContent>
    </Dialog>
  );
}
